/**
 * Prototype-only mapping from a federated run's pipeline + dataset selection
 * to a backend-generated mock HTML result card.
 *
 * Source HTML cards (read-only artifacts authored by the backend agent):
 *   /Users/wei/coding_claude/FederateX_agent_sync/Backend_To_Frontend/
 *     - Classification_LIDC_card.html
 *     - Segmentation_LIDC_card.html
 *     - Segmentation_ProStateX_card.html
 *     - Tabular_card.html
 *
 * Copied into this prototype at:
 *   app/public/mock-result-cards/*.html
 *
 * The URLs below are served by Vite at `http://localhost:3010/mock-result-cards/*`.
 * They are static HTML — no backend round-trip, no /runs/{run_id}/artifacts call.
 * In the real product this lookup will be replaced by a backend artifact URL
 * surfaced on the run record.
 */

export const MOCK_RESULT_CARDS = {
  tabular: "/mock-result-cards/Tabular_card.html",
  classification_lidc: "/mock-result-cards/Classification_LIDC_card.html",
  segmentation_lidc: "/mock-result-cards/Segmentation_LIDC_card.html",
  segmentation_prostatex: "/mock-result-cards/Segmentation_ProStateX_card.html",
};

/**
 * Curated per-card summary used by the inline Model Summary panel on /execute.
 * Values mirror exactly what each HTML card's hero section shows so the inline
 * summary and the full card stay in sync — when you open the modal you see
 * the same numbers in a richer layout.
 *
 *  Fields:
 *    title         — pipeline + model headline (matches card hero h2)
 *    dataset       — dataset display name
 *    subtitle      — `2 clients · N rounds · ...` line
 *    heroMetrics[] — KPI cells. `primary:true` highlights the selection metric
 *                    in the panel. Up to 4 cells rendered as the hero grid.
 *    federatedMeta — { strategy, rounds, clients, localEpochs }
 *    thresholdNote — small caption under the metrics (classification only)
 *    labelDef      — short label-definition string (optional)
 *    includes[]    — bullet list of "the full card also includes ..." items
 *                    rendered below the hero grid
 *
 * The numbers below are taken verbatim from the four HTML cards under
 * app/public/mock-result-cards/. Treat them as illustrative anchors — when
 * backend ships a real /artifacts endpoint these will come from the run
 * record instead.
 */
export const MOCK_RESULT_SUMMARIES = {
  tabular: {
    title: "Tabular Classification · FedAvg",
    dataset: "dataset_brca_survival",
    subtitle: "61 features · 2 classes",
    heroMetrics: [
      { label: "Model", value: "LOGREG", text: true },
      { label: "ROC-AUC", value: "0.614" },
      { label: "PR-AUC", value: "0.279", primary: true, hint: "Selection metric" },
      { label: "Best Round", value: "R2", hint: "of 2 rounds" },
    ],
    federatedMeta: { strategy: "FedAvg", rounds: 2, clients: 2, localEpochs: 2 },
    thresholdNote: "max_f1 · thr = 0.5300",
    labelDef: "Positive: Deceased · Negative: Alive · 34 pos · 184 neg · 15.6% positive",
    includes: [
      "Confusion matrix + metrics at recommended threshold (F1, precision, recall, specificity, balanced accuracy)",
      "ROC curve and Precision-Recall curve (global)",
      "Metrics over rounds, feature importance (top 10)",
      "Run metadata block",
    ],
  },
  classification_lidc: {
    title: "Imaging Classification · RESNET18",
    dataset: "LIDC-IDRI",
    subtitle: "2 clients · 2 rounds · n=148 · 33.1% malignant",
    heroMetrics: [
      { label: "Model", value: "RESNET18", text: true },
      { label: "ROC-AUC", value: "0.452" },
      { label: "PR-AUC", value: "0.325" },
      { label: "F1", value: "0.503", primary: true, hint: "at recommended thr" },
    ],
    federatedMeta: { strategy: "FedAvg", rounds: 2, clients: 2, localEpochs: 1 },
    thresholdNote: "max_f1 · thr = 0.2500",
    labelDef: "Positive: malignant · Negative: benign · 49 pos · 99 neg · 33.1% positive",
    includes: [
      "Confusion matrix at threshold 0.25",
      "ROC curve and Precision-Recall curve (global, server-aggregated)",
      "Metrics over rounds",
      "Run metadata block",
    ],
  },
  segmentation_lidc: {
    title: "Imaging Segmentation · UNET",
    dataset: "LIDC-IDRI",
    subtitle: "2 clients · 10 rounds · best round 10 · binary 2D",
    heroMetrics: [
      { label: "Model", value: "UNET", text: true },
      { label: "Best Dice (soft)", value: "0.5865", primary: true, hint: "@ round 10" },
      { label: "Final Dice (hard)", value: "0.5877" },
      { label: "Final IoU (hard)", value: "0.4161" },
    ],
    federatedMeta: {
      strategy: "FedAvg (segmentation)",
      rounds: 10,
      clients: 2,
      localEpochs: 2,
    },
    thresholdNote: "Final loss: 1.021 (BCE+Dice)",
    labelDef: "0: background · 1: lung nodule (single-channel binary mask)",
    includes: [
      "Loss over rounds, Dice (soft / hard) and IoU (hard) over rounds plots",
      "Sample mask examples · dev preview on LIDC-IDRI",
      "Run metadata block",
    ],
  },
  segmentation_prostatex: {
    title: "Imaging Segmentation · UNET",
    dataset: "ProstateX",
    subtitle: "2 clients · 10 rounds · best round 7 · binary 2D",
    heroMetrics: [
      { label: "Model", value: "UNET", text: true },
      { label: "Best Dice (soft)", value: "0.7015", primary: true, hint: "@ round 7" },
      { label: "Final Dice (hard)", value: "0.7031" },
      { label: "Final IoU (hard)", value: "0.5422" },
    ],
    federatedMeta: {
      strategy: "FedAvg (segmentation)",
      rounds: 10,
      clients: 2,
      localEpochs: 2,
    },
    thresholdNote: "Final loss: 0.834 (BCE+Dice)",
    labelDef: "0: background · 1: prostate lesion (single-channel binary mask)",
    includes: [
      "Loss over rounds, Dice (soft / hard) and IoU (hard) over rounds plots",
      "Sample mask examples · dev preview on ProstateX",
      "Run metadata block",
    ],
  },
};

/** Lookup a curated summary by pipelineKey (the key returned by
 *  resolveMockResultCard). Returns null if the key is unknown. */
export const getMockResultSummary = (pipelineKey) =>
  MOCK_RESULT_SUMMARIES[pipelineKey] || null;

const containsLidc = (s) => /(^|[^a-z0-9])lidc([^a-z0-9]|$)/i.test(s || "");
const containsProstateX = (s) => /prostatex/i.test(s || "");

/**
 * Pick the best-matching mock card.
 *
 *  @param {object} args
 *  @param {string=} args.processingType   "tabular" / "imaging_classification" /
 *                                          "imaging_segmentation" / "segmentation"
 *  @param {string=} args.imagingTask      Optional secondary hint:
 *                                          "classification" / "segmentation"
 *  @param {string=} args.datasetRef       A single dataset_ref hint (e.g.
 *                                          "lidc_seg_v1", "prostatex_cls_v1",
 *                                          "tabular/primary_site_oncology_v1")
 *  @param {Array<{site_id?:string,capability_id?:string,dataset_ref?:string}>=}
 *           args.selectedSites            Optional — used to derive datasetRef
 *                                          via siteCapabilities when datasetRef
 *                                          is not passed directly
 *  @param {object=} args.siteCapabilities Optional siteCapabilities map (from
 *                                          AppContext) — `{[site_id]: caps[]}`
 *  @returns {{ url: string, key: string, label: string }} chosen card meta
 */
export const resolveMockResultCard = ({
  processingType,
  imagingTask,
  datasetRef,
  selectedSites,
  siteCapabilities,
} = {}) => {
  const norm = (processingType || "").toString().toLowerCase();
  const task = (imagingTask || "").toString().toLowerCase();

  // Derive a dataset_ref to inspect from selectedSites if not supplied.
  let ref = datasetRef || "";
  if (!ref && Array.isArray(selectedSites) && siteCapabilities) {
    for (const sel of selectedSites) {
      if (!sel || !sel.site_id || !sel.capability_id) continue;
      const caps = siteCapabilities[sel.site_id] || [];
      const match = caps.find((c) => c.capability_id === sel.capability_id);
      if (match && match.dataset_ref) {
        ref = match.dataset_ref;
        break;
      }
    }
  }

  // ── Tabular ──────────────────────────────────────────────────────────
  if (norm === "tabular" || /^tabular[/_]/.test(ref)) {
    return {
      url: MOCK_RESULT_CARDS.tabular,
      key: "tabular",
      label: "Tabular Tier 1 · Result Card",
    };
  }

  // ── Classification (imaging) ────────────────────────────────────────
  const isClassification =
    norm === "imaging_classification" ||
    (norm === "imaging" && task === "classification") ||
    /_cls(_|$)/.test(ref);
  if (isClassification) {
    // dev-0.7.46+: both ProstateX and LIDC classification artifacts are
    // authored and live. Label collapsed to a single neutral string so
    // the caption no longer carries the historic "card not yet authored"
    // note. The underlying mock card asset stays LIDC-shaped — real runs
    // hit the backend Tier1 endpoint via taskId instead anyway.
    return {
      url: MOCK_RESULT_CARDS.classification_lidc,
      key: "classification_lidc",
      label: "Imaging Classification · Tier 1 result card",
    };
  }

  // ── Segmentation (imaging) ──────────────────────────────────────────
  const isSegmentation =
    norm === "imaging_segmentation" ||
    norm === "segmentation" ||
    (norm === "imaging" && task === "segmentation") ||
    /_seg(_|$)/.test(ref);
  if (isSegmentation) {
    // dev-0.7.46+: label collapsed to neutral "Tier 1 result card" wording
    // (same change applied to classification labels). Real runs hit the
    // backend Tier1 endpoint via taskId; the mock asset URL still picks
    // the matching dataset variant for offline preview.
    if (containsProstateX(ref)) {
      return {
        url: MOCK_RESULT_CARDS.segmentation_prostatex,
        key: "segmentation_prostatex",
        label: "Imaging Segmentation · Tier 1 result card",
      };
    }
    if (containsLidc(ref)) {
      return {
        url: MOCK_RESULT_CARDS.segmentation_lidc,
        key: "segmentation_lidc",
        label: "Imaging Segmentation · Tier 1 result card",
      };
    }
    // Default segmentation when dataset_ref doesn't disambiguate: LIDC card.
    return {
      url: MOCK_RESULT_CARDS.segmentation_lidc,
      key: "segmentation_lidc",
      label: "Imaging Segmentation · Tier 1 result card",
    };
  }

  // ── Fallback ────────────────────────────────────────────────────────
  return {
    url: MOCK_RESULT_CARDS.tabular,
    key: "tabular",
    label: "Tabular Tier 1 · Result Card (fallback)",
  };
};
